import { Alert, Checkbox, Input, Select, Space, Table, Tag, Tooltip, Typography } from "antd";
import type { ColumnsType, TablePaginationConfig } from "antd/es/table";
import type { SorterResult } from "antd/es/table/interface";
import moment from "moment";
import { useCallback, useEffect, useState } from "react";
import { fetchIncomingStock } from "../../apis/inventory.api";
import {
  IIncomingStockFilters,
  IIncomingStockGroup,
  IIncomingStockItem,
  IIncomingStockParams,
  IIncomingStockSummary,
} from "../../interfaces/inventory.interface";

// Purchase orders open in Zoho Books (Haut Luxe org); the team works out of Books, not Inventory.
export const ZOHO_PO_URL = "https://books.zoho.in/app/60039146748#/purchaseorders";
const PAGE_SIZE = 20;

// Item-row columns the server can sort on, keyed by the column key.
const SORTABLE: Record<string, string> = {
  delivery_date: "delivery_date",
  quantity_outstanding: "quantity_outstanding",
  name: "name",
  po_count: "po_count",
};

export const RECEIVE_STATUS_COLOR: Record<string, string> = {
  to_be_received: "default",
  in_transit: "blue",
  partially_received: "orange",
  received: "green",
};

export const qty = (value: string | null | undefined) => {
  const n = Number(value || 0);
  return Number.isInteger(n) ? n.toString() : n.toFixed(2);
};

export const fmtDate = (date: string | null) => (date ? moment(date).format("DD MMM YYYY") : "-");

const poLink = (line: IIncomingStockItem) => (
  <a href={`${ZOHO_PO_URL}/${line.zoho_purchase_order_id}`} target="_blank" rel="noreferrer">
    {line.purchase_order_number}
  </a>
);

export function DeliveryDate({ date, poDate }: { date: string | null; poDate: string | null }) {
  if (!date) {
    // No delivery date on the PO: say how long it has been open instead.
    const age = poDate ? moment().startOf("day").diff(moment(poDate), "days") : null;
    return (
      <Tooltip title="No delivery date on this PO in Zoho">
        <Typography.Text type="secondary">
          Not set{age !== null ? ` · open ${age}d` : ""}
        </Typography.Text>
      </Tooltip>
    );
  }
  const days = moment(date).startOf("day").diff(moment().startOf("day"), "days");
  let tag = <Tag color="green">in {days}d</Tag>;
  if (days < 0) tag = <Tag color="red">{-days}d overdue</Tag>;
  else if (days === 0) tag = <Tag color="gold">today</Tag>;
  else if (days <= 3) tag = <Tag color="orange">in {days}d</Tag>;
  return (
    <Space size={4} direction="vertical">
      <span>{fmtDate(date)}</span>
      {tag}
    </Space>
  );
}

// One child row per PO, shown when an item is coming in on more than one PO.
const lineColumns: ColumnsType<IIncomingStockItem> = [
  {
    title: "Delivery Date",
    dataIndex: "delivery_date",
    key: "delivery_date",
    width: 150,
    render: (date: string | null, line) => <DeliveryDate date={date} poDate={line.po_date} />,
  },
  { title: "PO", key: "po", width: 130, render: (_: unknown, line) => poLink(line) },
  { title: "Vendor", dataIndex: "vendor_name", key: "vendor_name", width: 220 },
  {
    title: "Destination",
    dataIndex: "location_name",
    key: "location_name",
    width: 200,
    render: (value: string, line) => value || line.warehouse_name || "-",
  },
  {
    title: "Outstanding",
    dataIndex: "quantity_outstanding",
    key: "quantity_outstanding",
    width: 120,
    render: (value: string, line) => (
      <strong>
        {qty(value)} {line.unit}
      </strong>
    ),
  },
  {
    title: "In Transit",
    dataIndex: "quantity_intransit",
    key: "quantity_intransit",
    width: 100,
    render: (value: string) => (Number(value) > 0 ? <Tag color="blue">{qty(value)}</Tag> : "-"),
  },
  {
    title: "Ordered / Received",
    key: "ordered",
    width: 150,
    render: (_: unknown, line) => `${qty(line.quantity)} / ${qty(line.quantity_received)}`,
  },
  {
    title: "PO Receive Status",
    dataIndex: "received_status_formatted",
    key: "received_status_formatted",
    width: 170,
    render: (label: string, line) => (
      <Tag color={RECEIVE_STATUS_COLOR[line.received_status] || "default"}>{label || "-"}</Tag>
    ),
  },
  { title: "PO Date", dataIndex: "po_date", key: "po_date", width: 120, render: fmtDate },
  {
    title: "Rate",
    dataIndex: "rate",
    key: "rate",
    width: 110,
    render: (value: string) =>
      `₹${Number(value || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`,
  },
];

export default function IncomingStockTable() {
  const [rows, setRows] = useState<IIncomingStockGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [summary, setSummary] = useState<IIncomingStockSummary | null>(null);
  const [filterOptions, setFilterOptions] = useState<IIncomingStockFilters>({
    locations: [],
    vendors: [],
  });
  const [searchText, setSearchText] = useState("");
  const [params, setParams] = useState<IIncomingStockParams>({
    page: 1,
    page_size: PAGE_SIZE,
    ordering: "delivery_date",
  });

  const load = useCallback(async (p: IIncomingStockParams) => {
    setLoading(true);
    const response = await fetchIncomingStock(p);
    if (response) {
      setRows(response.data);
      setTotal(response.meta.pagination.count);
      setSummary(response.meta.summary);
      setFilterOptions(response.meta.filters);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    load(params);
  }, [params, load]);

  // Any filter change goes back to page 1.
  const update = (patch: Partial<IIncomingStockParams>) =>
    setParams((prev) => ({ ...prev, page: 1, ...patch }));

  const handleTableChange = (
    pagination: TablePaginationConfig,
    _filters: unknown,
    sorter: SorterResult<IIncomingStockGroup> | SorterResult<IIncomingStockGroup>[]
  ) => {
    const s = Array.isArray(sorter) ? sorter[0] : sorter;
    const field = s?.order ? SORTABLE[String(s.columnKey)] : undefined;
    const ordering = field ? `${s.order === "descend" ? "-" : ""}${field}` : "delivery_date";
    setParams((prev) => ({
      ...prev,
      page: ordering === prev.ordering ? pagination.current || 1 : 1,
      ordering,
    }));
  };

  const sortOrderFor = (field: string) => {
    if (params.ordering === field) return "ascend" as const;
    if (params.ordering === `-${field}`) return "descend" as const;
    return null;
  };

  // A row on a single PO shows that PO inline; a row on several summarises them and expands.
  const single = (row: IIncomingStockGroup) => (row.lines.length === 1 ? row.lines[0] : null);

  const columns: ColumnsType<IIncomingStockGroup> = [
    {
      title: "Item",
      dataIndex: "name",
      key: "name",
      width: 360,
      fixed: "left",
      sorter: true,
      sortOrder: sortOrderFor("name"),
      render: (name: string, row) => (
        <Space direction="vertical" size={0}>
          <span>{name}</span>
          {row.variant_handle ? (
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {row.variant_handle}
            </Typography.Text>
          ) : (
            <Tooltip title="No variant on our catalogue carries this Zoho item id">
              <Tag color="warning" style={{ fontSize: 11 }}>
                not linked to a variant
              </Tag>
            </Tooltip>
          )}
        </Space>
      ),
    },
    {
      title: "Delivery Date",
      key: "delivery_date",
      width: 160,
      sorter: true,
      sortOrder: sortOrderFor("delivery_date"),
      render: (_: unknown, row) => {
        const line = single(row);
        if (line) return <DeliveryDate date={line.delivery_date} poDate={line.po_date} />;
        return (
          <Space direction="vertical" size={0}>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              earliest
            </Typography.Text>
            <DeliveryDate date={row.earliest_delivery_date} poDate={row.oldest_po_date} />
          </Space>
        );
      },
    },
    {
      title: "Outstanding",
      key: "quantity_outstanding",
      width: 130,
      sorter: true,
      sortOrder: sortOrderFor("quantity_outstanding"),
      render: (_: unknown, row) => (
        <strong>
          {qty(row.quantity_outstanding)} {row.unit}
        </strong>
      ),
    },
    {
      title: "In Transit",
      key: "quantity_intransit",
      width: 100,
      render: (_: unknown, row) =>
        Number(row.quantity_intransit) > 0 ? <Tag color="blue">{qty(row.quantity_intransit)}</Tag> : "-",
    },
    {
      title: "POs",
      key: "po_count",
      width: 130,
      sorter: true,
      sortOrder: sortOrderFor("po_count"),
      render: (_: unknown, row) => {
        const line = single(row);
        return line ? poLink(line) : <Tag color="purple">{row.po_count} POs</Tag>;
      },
    },
    {
      title: "Vendor",
      key: "vendor",
      width: 220,
      render: (_: unknown, row) =>
        row.vendors.length === 1 ? (
          row.vendors[0]
        ) : (
          <Tooltip title={row.vendors.join(", ")}>
            <span>{row.vendors.length} vendors</span>
          </Tooltip>
        ),
    },
    {
      title: "Destination",
      key: "destination",
      width: 220,
      render: (_: unknown, row) =>
        row.destinations.length <= 1 ? (
          row.destinations[0] || "-"
        ) : (
          <Tooltip title={row.destinations.join(", ")}>
            <span>{row.destinations.length} destinations</span>
          </Tooltip>
        ),
    },
    {
      title: "PO Receive Status",
      key: "received_status",
      width: 170,
      render: (_: unknown, row) => {
        const line = single(row);
        if (!line) return "-";
        return (
          <Tag color={RECEIVE_STATUS_COLOR[line.received_status] || "default"}>
            {line.received_status_formatted || "-"}
          </Tag>
        );
      },
    },
    {
      title: "Ordered / Received",
      key: "ordered",
      width: 150,
      render: (_: unknown, row) => `${qty(row.quantity)} / ${qty(row.quantity_received)}`,
    },
  ];

  return (
    <div>
      {summary && summary.pos_awaiting_items > 0 && (
        <Alert
          className="mb-3"
          type="info"
          showIcon
          message={`${summary.pos_awaiting_items} purchase order${
            summary.pos_awaiting_items === 1 ? " is" : "s are"
          } still waiting for their items to be pulled from Zoho, so this list may be incomplete. The sync catches up every 15 minutes.`}
        />
      )}

      <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 pb-3">
        <Space wrap>
          <Input.Search
            value={searchText}
            onChange={(ev) => setSearchText(ev.target.value)}
            onSearch={(value) => update({ search: value })}
            style={{ width: 300 }}
            placeholder="Search item, variant, PO number, vendor..."
            allowClear
          />
          <Select
            mode="multiple"
            allowClear
            style={{ minWidth: 220 }}
            placeholder="All destinations"
            value={params.location_id || []}
            onChange={(value: string[]) => update({ location_id: value })}
            options={filterOptions.locations.map((l) => ({
              value: l.zoho_location_id,
              label: `${l.location_name} (${l.count})`,
            }))}
            maxTagCount="responsive"
          />
          <Select
            mode="multiple"
            allowClear
            showSearch
            optionFilterProp="label"
            style={{ minWidth: 220 }}
            placeholder="All vendors"
            value={params.zoho_vendor_id || []}
            onChange={(value: string[]) => update({ zoho_vendor_id: value })}
            options={filterOptions.vendors.map((v) => ({
              value: v.zoho_vendor_id,
              label: `${v.vendor_name} (${v.count})`,
            }))}
            maxTagCount="responsive"
          />
          <Checkbox checked={!!params.overdue} onChange={(ev) => update({ overdue: ev.target.checked })}>
            Overdue only
          </Checkbox>
          <Checkbox
            checked={!!params.in_transit_only}
            onChange={(ev) => update({ in_transit_only: ev.target.checked })}
          >
            In transit only
          </Checkbox>
          <Checkbox
            checked={!!params.include_received}
            onChange={(ev) => update({ include_received: ev.target.checked })}
          >
            Include fully received lines
          </Checkbox>
        </Space>
        {summary && (
          <Typography.Text type="secondary">
            {summary.items} item{summary.items === 1 ? "" : "s"} on {summary.purchase_orders} PO
            {summary.purchase_orders === 1 ? "" : "s"} · {qty(summary.quantity_outstanding)} units
            outstanding
          </Typography.Text>
        )}
      </div>

      <Table
        dataSource={rows}
        loading={loading}
        columns={columns}
        rowKey="key"
        scroll={{ x: 1650 }}
        onChange={handleTableChange}
        expandable={{
          rowExpandable: (row) => row.lines.length > 1,
          expandedRowRender: (row) => (
            <Table
              dataSource={row.lines}
              columns={lineColumns}
              rowKey="id"
              size="small"
              pagination={false}
              scroll={{ x: 1470 }}
            />
          ),
        }}
        pagination={{
          current: params.page,
          pageSize: PAGE_SIZE,
          total,
          showSizeChanger: false,
          showTotal: (count, range) => `${range[0]}-${range[1]} of ${count} items`,
        }}
      />
    </div>
  );
}
